import { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import styled, { css, keyframes, useTheme } from 'styled-components'
import { Box, Flex } from '@/components/atoms/Grid'
import { useWindowSize } from '@react-hook/window-size/throttled'
import useDisableScroll from 'hooks/useDisableScroll'
import { HeaderNotice, HomeBanner, NavigationItem } from '@/network/graphql.g'
import { useMainNavigationDispatch } from '@/providers/mainNavigationMenuProvider'
import { NavigationBackIconDefault } from '@/components/atoms/icons/NavigationBackIconDefault'
import { Asset, useAsset } from '@/providers/assetsProvider'
import Image from 'next/image'
import { MenuCloseIconDefault } from '@/components/atoms/icons/MenuCloseIconDefault'
import {
  lastVisitedMainCategoryKey,
  MainCategoryId
} from '../../../hooks/useCurrentMainCategoryId'

const AnimationDuration = 0.3
const NavigationBarHeight = 50
const NavigationHeight = 74

type SlidePageRedesign = {
  menuWidth?: number | string
  page: JSX.Element
}

type SlideMenuRedesignProps = {
  onDidClose?: () => void
  pages: SlidePageRedesign[]
  modal?: boolean
  showSlideMenu?: boolean
  setShowSlideMenu?: React.Dispatch<React.SetStateAction<boolean>>
  navigationItems?: NavigationItem[]
  onCategoryClick?: (
    id: string,
    subcategories: NavigationItem[],
    banners: HomeBanner[]
  ) => void
  showHeaderPromo?: boolean
  headerNotice?: HeaderNotice
  selectedSubcategory?: NavigationItem
}

export type SlideMenuActions = {
  slideNext: () => void
  close: () => void
}

const MainSlideMenuPagesRedesign = forwardRef<
  SlideMenuActions,
  SlideMenuRedesignProps
>(
  (
    {
      pages,
      modal = true,
      showSlideMenu,
      setShowSlideMenu,
      navigationItems,
      onCategoryClick,
      showHeaderPromo,
      headerNotice,
      selectedSubcategory
    },
    ref
  ) => {
    const [presentInDom, setPresentInDom] = useState<boolean>(false)
    const [, height] = useWindowSize()
    const [activeIndex, setActiveIndex] = useState<number>(0)
    const headerPromoHeight = showHeaderPromo && headerNotice !== null ? 50 : 0
    const { slugPath } = useMainNavigationDispatch()
    const [activeCategoryId, setActiveCategoryId] = useState<string>(null)

    useEffect(() => {
      try {
        const stored = localStorage.getItem(lastVisitedMainCategoryKey)
        if (stored) {
          setActiveCategoryId(stored)
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }, [])

    const firstCategory = navigationItems.filter(
      ({ isMainCategory }) => isMainCategory
    )[0]

    useImperativeHandle(ref, () => ({
      close: handleClose,
      slideNext: () => {
        setActiveIndex(1)
      }
    }))

    useEffect(() => {
      setPresentInDom(true)
    }, [])

    const handleClickBack = () => {
      if (activeIndex === 0) {
        handleClose()
        return
      }
      setActiveIndex((prevIndex) => prevIndex - 1)
    }
    const handleClose = () => {
      setShowSlideMenu(false)
      setTimeout(() => {
        setPresentInDom(false)
      }, AnimationDuration * 1000)
    }

    const handleCategoryClick = (categoryId, children, banners) => {
      onCategoryClick(categoryId, children, banners)
      setActiveCategoryId(categoryId)
      setActiveIndex(0)
      try {
        localStorage.setItem(lastVisitedMainCategoryKey, categoryId)
        // eslint-disable-next-line no-empty
      } catch {}
    }

    useDisableScroll(modal || showSlideMenu)

    useEffect(() => {
      const pageWrappers = document.querySelectorAll('.page-wrapper')
      pageWrappers.forEach((wrapper) => (wrapper.scrollTop = 0))
    }, [activeCategoryId])

    const { asset } = useAsset()
    const theme = useTheme()

    return !presentInDom ? null : (
      <MobileMenuWrapper show={showSlideMenu}>
        <MenuItemsWrapper show={showSlideMenu}>
          <Navigation>
            <NavInner>
              <LogoWrap>
                <Image
                  src={asset(Asset.MobileMenuLogo, true)}
                  width={theme.logoWidth}
                  height={theme.logoHeigth}
                  data-testid="mobileMenuLogo"
                  unoptimized
                />
              </LogoWrap>
              <Flex justifyContent="center">
                <SegmentedBar role="tablist">
                  {navigationItems &&
                    navigationItems
                      .filter(
                        ({ isMainCategory, categoryId }) =>
                          isMainCategory &&
                          categoryId !== MainCategoryId.Sports &&
                          categoryId !== MainCategoryId.Accessories
                      )
                      .map(
                        ({ name, url, categoryId, children, homeBanners }) => {
                          const isActive =
                            (!!activeCategoryId &&
                              activeCategoryId === categoryId) ||
                            (!activeCategoryId &&
                              (slugPath.find((p) => p.id === categoryId) ||
                                (slugPath.length === 0 &&
                                  firstCategory?.categoryId === categoryId)))

                          return (
                            <SegmentedItem
                              key={url}
                              role="tab"
                              aria-selected={isActive}
                              $active={isActive}
                              onClick={() =>
                                handleCategoryClick(
                                  categoryId,
                                  children,
                                  homeBanners
                                )
                              }
                            >
                              {name.charAt(0).toUpperCase() + name.slice(1)}
                            </SegmentedItem>
                          )
                        }
                      )}
                </SegmentedBar>
              </Flex>
              <MenuCloseIconDefault
                mr={3}
                onClick={handleClose}
                cursorOnHover={true}
                data-testid="mobileMenuCloseIcon"
                strokeWidth={1.5}
              />
            </NavInner>
          </Navigation>

          <SubCategoriesWrapper
            menuHeight={height}
            headerPromoHeight={headerPromoHeight}
          >
            {pages.map(({ page }, index) => (
              <PageWrapper
                key={page.key}
                active={index === activeIndex}
                index={index}
                className="page-wrapper"
              >
                {index !== 0 && (
                  <SubcategoryTitleButton onClick={handleClickBack}>
                    <StyledNavigationBackIconDefault />
                    <SubcategoryTitle>
                      {selectedSubcategory?.name}
                    </SubcategoryTitle>
                  </SubcategoryTitleButton>
                )}
                <Box p={15}>{page}</Box>
              </PageWrapper>
            ))}
          </SubCategoriesWrapper>
        </MenuItemsWrapper>
        {modal && <Overlay show={showSlideMenu} onClick={handleClose} />}
      </MobileMenuWrapper>
    )
  }
)

export default MainSlideMenuPagesRedesign

const animateIn = keyframes`
   0% {
   opacity: 0;
   visibility: hidden;
  }
  100% {
    opacity: 1;
    visibility: visible;
  }
`

const slideInFromRight = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
`

const MobileMenuWrapper = styled(Flex)`
  position: absolute;
  right: 0;
  z-index: 1002;
  top: 0;
  width: 100vw;
  height: 100vh;
`

type MountedProps = {
  show: boolean
}

const MenuItemsWrapper = styled<MountedProps>(Box)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.background};
  ${({ show }) =>
    show &&
    css`
      animation: ${animateIn} ${AnimationDuration}s ease;
    `}
  z-index: 11;
`

const PageWrapper = styled.div`
  width: 100%;
  position: ${(props) => (props.index === 0 ? 'relative' : 'absolute')};
  top: 0;
  left: 0;
  height: 100%;
  background: ${(props) => props.theme.colors.background};
  z-index: ${(props) => (props.index === 1 ? 2 : 1)};
  transform: ${(props) =>
    props.index === 0
      ? 'translateX(0)'
      : props.active
      ? 'translateX(0)'
      : 'translateX(100%)'};
  transition: ${(props) => !props.active && 'transform 0.6s'};
  ${(props) =>
    props.index !== 0 &&
    props.active &&
    css`
      animation: ${slideInFromRight} 0.6s;
    `}
  overflow-y: auto;
`

const Overlay = styled<MountedProps>(Box)`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  position: fixed;
  z-index: -1;
  left: 0;
  bottom: 0;
  top: 0;
  right: 0;
  ${({ show }) =>
    show &&
    css`
      animation: ${animateIn} ${AnimationDuration}s ease;
    `}
`

const SubCategoriesWrapper = styled(Box)`
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  height: ${(props) =>
    props.menuHeight &&
    `${
      props.menuHeight -
      (NavigationBarHeight + NavigationHeight + props.headerPromoHeight)
    }px`};
`

const SubcategoryTitleButton = styled.button`
  border: none;
  outline: none;
  background-color: transparent;
  width: 100%;
  margin: 0;
  padding: 0;
  position: relative;
`

const SubcategoryTitle = styled.h2`
  padding: 15px;
  margin: 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.navMenuGrey};
  display: block;
  font-size: 14px;
  font-weight: 500;
  position: relative;
  text-align: center;
  color: ${(props) => props.theme.colors.towerColorSecondary};
`

const StyledNavigationBackIconDefault = styled(NavigationBackIconDefault)`
  position: absolute;
  height: 16px;
  width: 10px;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
`

const Navigation = styled.nav`
  position: sticky;
  top: 0;
  z-index: 12;
  background: ${(props) => props.theme.colors.background};
  border-bottom: 1px solid
    ${(props) => props.theme.colors.menuItemBackgroundSecondary};
`

const NavInner = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
`

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  img {
    height: 22px;
    display: block;
  }
`

const SegmentedBar = styled.div`
  display: inline-flex;
  gap: 8px;
  padding: 2px;
  background: ${(props) => props.theme.colors.navMenuGrey};
  border-radius: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`

const SegmentedItem = styled.button<{ $active?: boolean }>`
  position: relative;
  border: 0;
  outline: 0;
  -webkit-appearance: none;
  color: ${(props) => props.theme.colors.towerColorSecondary};
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
  text-transform: none;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  box-shadow: ${({ $active }) =>
    $active ? '0 2px 6px rgba(0,0,0,0.12)' : 'none'};
  transform: translateZ(0);
  transition: background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;

  &:active {
    transform: translateY(1px);
  }
`
